import React, {useCallback, useContext, useMemo, useState} from 'react';

import {MessageContext} from 'src/contexts/message-context';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons, Sizes} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';
import {Tooltip} from '@pb/kit-component-popups';
import {useShown} from '@pb/kit-hooks-use-shown';


import {usePasswordSharingWarningEmail} from './usePasswordSharingWarningEmail';


type Props = {
    userId: number,
    onFieldChange: (fieldName: string) => void,
    disabled?: boolean,
};

const MODAL_CAPTION = 'Send Password Sharing Warning Email';

export const SendPasswordSharingWarningEmail = ({userId, onFieldChange, disabled}: Props) => {
    const {setSuccessMessage} = useContext(MessageContext);
    const {shown, onClick, onClose} = useShown();

    const onSuccess = useCallback(() => {
        onFieldChange(MODAL_CAPTION);
        onClose();
        setSuccessMessage('Password Sharing Warning email sent successfully');
    }, [onFieldChange, setSuccessMessage, onClose]);

    const tooltipMessage = useMemo(() => {
        if (disabled) {
            return 'This option is disabled for currently inactive users.';
        }
        return '';
    }, [disabled]);

    return (
        <div>
            <Tooltip
                anchor={({anchorRef, getAnchorProps}) => {
                    return (
                        <Button
                            ref={anchorRef}
                            {...getAnchorProps()}
                            startIcon={<Icon type={Icons.ERROR} size={Sizes.Size_14}/>}
                            onClick={onClick}
                            disabled={disabled}
                            variant="Type.SECONDARY">
                            Send Sharing Warning Email
                        </Button>
                    );
                }}>
                {tooltipMessage}
            </Tooltip>
            <Dialog
                shown={shown}
                setShown={onClose}>
                <SendPasswordSharingWarningEmailModal
                    userId={userId}
                    onClose={onClose}
                    onSuccess={onSuccess}
                />
            </Dialog>
        </div>
    );
};

type ModalProps = {
    userId: number,
    onClose: () => void,
    onSuccess: () => void,
};

const MODAL_WIDTH = 450;

const SendPasswordSharingWarningEmailModal = ({userId, onClose, onSuccess}: ModalProps) => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const {busy, event} = usePasswordSharingWarningEmail({
        userId,
        onSuccess,
        onFail: setErrorMessage,
    });

    const onClick = useCallback(() => {
        event();
    }, [event]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                {MODAL_CAPTION}
            </DialogHeader>
            <DialogMain style={{padding: 24, maxWidth: MODAL_WIDTH}}>
                {errorMessage ? <Message color="error">{errorMessage}</Message> : null}
                <p>
                    A Password Sharing Warning Email will be sent to the user who shares their passwords,
                    alerting them to the security policy violation and advising them on proper account usage.
                </p>
                <p>Do you want to send Password Sharing Warning Email?</p>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" busy={busy} onClick={onClick}>
                        Send
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
